import {
    KNOWN_TENANT_CODES,
    TenantError,
    type CreateTenantInput,
    type Tenant,
    type TenantErrorCode,
    type UpdateTenantInput,
} from '@/domain/tenants'
import type { TenantsApi } from '@/ports/tenants-api'

export class HttpTenantsApi implements TenantsApi {
    private readonly baseURL: string

    constructor(baseURL = '') {
        this.baseURL = baseURL.replace(/\/+$/, '')
    }

    async list(): Promise<Tenant[]> {
        const resp = await this.send('/api/v1/tenants', { method: 'GET' })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { items?: unknown }
        if (!Array.isArray(body.items)) {
            throw new TenantError('internal_error', 'Invalid /tenants response shape')
        }
        return body.items.map(parseTenant)
    }

    async create(input: CreateTenantInput, csrfToken: string): Promise<Tenant> {
        const payload: Record<string, unknown> = {
            id: input.id,
            name: input.name,
        }
        if (input.settings) {
            payload.settings = serializeSettings(input.settings)
        }
        const resp = await this.send('/api/v1/tenants', {
            method: 'POST',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify(payload),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { tenant?: unknown }
        return parseTenant(body.tenant)
    }

    async update(id: string, input: UpdateTenantInput, csrfToken: string): Promise<Tenant> {
        const payload: Record<string, unknown> = {}
        if (input.name !== undefined) payload.name = input.name
        if (input.settings) payload.settings = serializeSettings(input.settings)
        const resp = await this.send(`/api/v1/tenants/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify(payload),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { tenant?: unknown }
        return parseTenant(body.tenant)
    }

    async remove(id: string, csrfToken: string): Promise<void> {
        const resp = await this.send(`/api/v1/tenants/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: csrfHeaders(csrfToken),
        })
        if (resp.status === 204) return
        throw await readError(resp)
    }

    private send(path: string, init: RequestInit): Promise<Response> {
        return fetch(this.baseURL + path, {
            ...init,
            credentials: 'include',
            cache: 'no-store',
        }).catch((cause: unknown) => {
            const message = cause instanceof Error ? cause.message : 'fetch failed'
            throw new TenantError('network_error', message)
        })
    }
}

function csrfHeaders(token: string): Record<string, string> {
    return { 'Content-Type': 'application/json', 'X-CSRF-Token': token }
}

function serializeSettings(s: {
    sanitizationMode: string
    classifierEnabled: boolean
    auditContentMode: string
    responseCacheEnabled: boolean
    responseCacheTtlSeconds: number
}): Record<string, unknown> {
    return {
        sanitization_mode: s.sanitizationMode,
        classifier_enabled: s.classifierEnabled,
        audit_content_mode: s.auditContentMode,
        response_cache_enabled: s.responseCacheEnabled,
        response_cache_ttl_seconds: s.responseCacheTtlSeconds,
    }
}

function parseTenant(raw: unknown): Tenant {
    if (!raw || typeof raw !== 'object') {
        throw new TenantError('internal_error', 'Invalid tenant payload')
    }
    const obj = raw as Record<string, unknown>
    const s = (obj.settings as Record<string, unknown> | undefined) ?? {}
    return {
        id: stringField(obj.id),
        name: stringField(obj.name),
        createdAt: stringField(obj.created_at),
        settings: {
            sanitizationMode: stringField(s.sanitization_mode) || 'off',
            classifierEnabled: typeof s.classifier_enabled === 'boolean' ? s.classifier_enabled : false,
            auditContentMode: stringField(s.audit_content_mode) || 'zero',
            responseCacheEnabled: typeof s.response_cache_enabled === 'boolean' ? s.response_cache_enabled : false,
            responseCacheTtlSeconds: typeof s.response_cache_ttl_seconds === 'number' ? s.response_cache_ttl_seconds : 0,
        },
    }
}

function stringField(v: unknown): string {
    return typeof v === 'string' ? v : ''
}

async function readError(resp: Response): Promise<TenantError> {
    if (resp.status === 401) {
        return new TenantError('unauthorized', 'Session expired', 401)
    }
    if (resp.status === 403) {
        try {
            const body = (await resp.json()) as { error?: { code?: unknown } }
            const code = typeof body.error?.code === 'string' ? body.error.code : ''
            if (code === 'csrf_required' || code === 'csrf_invalid' || code === 'tenant_scope') {
                return new TenantError(code as TenantErrorCode, code, 403)
            }
        } catch {
            // fallback
        }
        return new TenantError('forbidden', 'Forbidden', 403)
    }
    let body: unknown = null
    try {
        body = await resp.json()
    } catch {
        return new TenantError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const err = (body as { error?: { code?: unknown; message?: unknown } }).error
    if (!err || typeof err !== 'object') {
        return new TenantError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const rawCode = typeof err.code === 'string' ? err.code : 'unknown'
    const code: TenantErrorCode = (KNOWN_TENANT_CODES as readonly string[]).includes(rawCode)
        ? (rawCode as TenantErrorCode)
        : 'unknown'
    const message = typeof err.message === 'string' ? err.message : `HTTP ${resp.status}`
    return new TenantError(code, message, resp.status)
}
